import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Check, X } from 'lucide-react';

import { VerificationStatus } from 'utils/bankStatements';


const InlineTransactionForm = ({
  accountId,
  onCancel,
  onAdd,
}) => {
  console.log('InlineTransactionForm rendering for account:', accountId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    type: 'Deposit',
    amount: '',
    description: '',
  });
  const [errors, setErrors] = useState({
    date: '',
    amount: '',
    description: '',
  });

  const validateDate = (dateStr) => {
    if (!dateStr) {
      return 'Please enter a transaction date';
    }

    const inputDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if it's a valid date
    if (Number.isNaN(inputDate.getTime())) {
      return 'Please enter a valid date';
    }

    // Check for future date
    if (inputDate > today) {
      return 'Transaction date cannot be in the future';
    }

    // Check for minimum date (7 years ago)
    const sevenYearsAgo = new Date();
    sevenYearsAgo.setFullYear(sevenYearsAgo.getFullYear() - 7);
    sevenYearsAgo.setHours(0, 0, 0, 0);

    if (inputDate < sevenYearsAgo) {
      return 'Transaction date cannot be older than 7 years';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      const dateValidationError = validateDate(formData.date);

      const validationErrors = {
        date: dateValidationError ?? '', // Convert null to empty string
        amount: !formData.amount
          ? 'Please enter a transaction amount'
          : Number.isNaN(parseFloat(formData.amount))
            ? 'Please enter a valid amount'
            : '',
        description: !formData.description.trim()
          ? 'Please enter a transaction description'
          : '',
      };

      // Check if there are any validation errors
      if (Object.values(validationErrors).some((error) => error !== '')) {
        setErrors(validationErrors);
        setIsSubmitting(false);
        return;
      }

      const amount = parseFloat(formData.amount);

      await onAdd({
        type: formData.type,
        date: formData.date,
        description: formData.description.trim(),
        depositAmount: formData.type === 'Deposit' ? amount : null,
        withdrawalAmount: formData.type === 'Withdrawal' ? amount : null,
        verificationStatus: VerificationStatus.Manual,
        lastVerifiedAt: new Date().toISOString(),
      });

      onCancel(); // Close the form after successful submission
    } catch (error) {
      console.error('Error adding transaction:', error);
      setErrors((prev) => ({
        ...prev,
        general: 'Unable to add transaction. Please try again.',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <tr className="bg-blue-50/50 border-t border-b border-gray-200">
      <td colSpan={1} className="p-4">
        <div className="flex flex-col gap-1">
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
            max={new Date().toISOString().split('T')[0]}
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '14px',
              lineHeight: '20px',
              color: '#374151',
              width: '90%',
              height: '32px',
              backgroundColor: 'white',
            }}
            required
          />
          {errors.date && (
            <div style={{ fontSize: '12px', color: '#EF4444' }}>
              {errors.date}
            </div>
          )}
        </div>
      </td>

      <td colSpan={1} className="p-4">
        <div
          style={{
            display: 'flex',
            gap: '1px',
            backgroundColor: '#E5E7EB',
            borderRadius: '6px',
            padding: '1px',
            border: '1px solid transparent',
            width: '100%',
            height: '32px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          }}
        >
          {(['Withdrawal', 'Deposit']).map((type) => {
            const isSelected = formData.type === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, type }))}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backgroundColor: isSelected ? 'white' : 'transparent',
                  color: isSelected ? '#2563EB' : '#6B7280',
                  boxShadow: isSelected
                    ? '0 1px 2px rgba(0, 0, 0, 0.05)'
                    : 'none',
                  padding: '0 8px',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '30px',
                }}
              >
                {type}
              </button>
            );
          })}
        </div>
      </td>

      <td colSpan={1} className="p-4">
        <div className="flex flex-col gap-1">
          <div className="relative">
            <input
              type="text"
              value={formData.amount}
              onChange={(e) => {
                // First, remove any non-numeric and non-decimal characters
                let value = e.target.value.replace(/[^0-9.]/g, '');

                // Handle decimal places
                const parts = value.split('.');
                if (parts.length > 2) {
                  // More than one decimal point - keep only first one
                  value = `${parts[0]}.${parts.slice(1).join('')}`;
                }
                if (parts.length === 2 && parts[1].length > 2) {
                  // More than 2 decimal places - truncate
                  value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                }

                setFormData((prev) => ({ ...prev, amount: value }));
              }}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '14px',
                lineHeight: '20px',
                color: '#374151',
                backgroundColor: 'white',
                width: '90%',
                height: '32px',
                textAlign: 'right',
              }}
              placeholder="0.00"
              required
            />
          </div>
          {errors.amount && (
            <div style={{ fontSize: '12px', color: '#EF4444' }}>
              {errors.amount}
            </div>
          )}
        </div>
      </td>

      <td colSpan={1} className="p-4">
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value.slice(0, 100) }))}
          style={{
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '14px',
            lineHeight: '20px',
            color: '#374151',
            backgroundColor: 'white',
            width: '100%',
            height: '32px',
          }}
          placeholder="Transaction description"
          maxLength={100}
          required
        />
        {errors.description && (
          <div style={{ fontSize: '12px', color: '#EF4444' }}>
            {errors.description}
          </div>
        )}
      </td>

      <td colSpan={2} className="p-4">
        <form onSubmit={handleSubmit} className="flex gap-2 justify-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="confidence-badge verified text-xs flex items-center h-8"
          >
            {isSubmitting ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
            ) : (
              <Check className="w-3.5 h-3.5 mr-1.5" />
            )}
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="confidence-badge review text-xs flex items-center h-8"
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Cancel
          </button>
        </form>
      </td>
    </tr>
  );
};

InlineTransactionForm.propTypes = {
  accountId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default InlineTransactionForm;
